import { DISCONTINUED_MAX_CENTS } from '../../types';
import { VARIANT_SCOPES } from './variantScopes';

type SupportedMarket = 'US' | 'CA';

const CURRENCY_BY_MARKET: Record<SupportedMarket, string> = {
    US: 'USD',
    CA: 'CAD',
};

/**
 * Catalog `onlineFrom-*` ltext is `YYYY-MM-DD HH:mm:ss`. ISO `T`/`Z` sorts after
 * space, so same-UTC-day values would all compare as already live.
 */
const toCatalogTimestamp = (date: Date): string => date.toISOString().slice(0, 19).replace('T', ' ');

/**
 * CT Product Projection where clause matching a variant that is PLP-eligible
 * for the given market: available SKU, not deleted, onlineFrom in the past, and
 * a market price above the discontinued-cents threshold Algolia applies.
 */
export const buildPlpEligibleVariantPredicate = (market: SupportedMarket, now: Date): string => {
    const marketSuffix = market.toLowerCase();
    const currency = CURRENCY_BY_MARKET[market];
    const nowCatalog = toCatalogTimestamp(now);
    const available = `attributes(name="isSKUAvailable-${marketSuffix}" and value=true)`;
    const notDeleted = `not attributes(name="deleteInd" and value=1)`;
    const onlineFromPast = `attributes(name="onlineFrom-${marketSuffix}" and value(en-US < "${nowCatalog}"))`;
    const hasLivePrice = `prices(value(centAmount > ${DISCONTINUED_MAX_CENTS} and currencyCode = "${currency}"))`;
    const notDiscontinuedPrice = `not prices(value(centAmount <= ${DISCONTINUED_MAX_CENTS} and currencyCode = "${currency}"))`;
    const variantInner = `${available} and ${notDeleted} and ${onlineFromPast} and ${hasLivePrice} and ${notDiscontinuedPrice}`;
    const buildScopedPredicate = (scope: string): string => `${scope}(${variantInner})`;

    return `(${VARIANT_SCOPES.map(buildScopedPredicate).join(' or ')})`;
};

export const buildActiveShopCriteriaGroupWhere = (criteriaWhere: string, market: SupportedMarket, now: Date): string =>
    `${criteriaWhere} and ${buildPlpEligibleVariantPredicate(market, now)}`;
